import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Navbar } from '@/components/feature/Navbar';
import { Footer } from '@/components/feature/Footer';
import { products } from '@/mocks/products';
import { useCartContext } from '@/hooks/useCartContext';

export default function ProductDetailPage() {
  const { id } = useParams();
  const product = products.find(p => p.id === Number(id));
  const { addToCart } = useCartContext();
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('description');
  const [added, setAdded] = useState(false);

  if (!product) return (
    <>
      <Navbar />
      <div className="pt-40 text-center text-stone-500 min-h-screen">Product not found. <Link to="/products" className="text-amber-600 underline">Back to products</Link></div>
      <Footer />
    </>
  );

  const handleAddToCart = () => {
    addToCart(product, quantity);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <>
      <Navbar />
      <main className="pt-28 pb-20 bg-white min-h-screen">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-stone-400 mb-8 flex-wrap">
            <Link to="/" className="hover:text-amber-600">Home</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/products" className="hover:text-amber-600">Products</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to={`/products?category=${product.category}`} className="hover:text-amber-600">{product.category}</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-stone-600 line-clamp-1">{product.name}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            {/* Images */}
            <div>
              <div className="relative overflow-hidden rounded-2xl bg-stone-50 mb-4" style={{ paddingBottom: '110%' }}>
                <img src={product.images[selectedImage]} alt={product.name}
                  className="absolute inset-0 w-full h-full object-cover object-top" />
                {product.badge && (
                  <span className="absolute top-4 left-4 bg-amber-500 text-white text-xs font-semibold px-3 py-1 rounded-full">{product.badge}</span>
                )}
              </div>
              <div className="grid grid-cols-6 gap-2">
                {product.images.map((img, i) => (
                  <button key={i} onClick={() => setSelectedImage(i)}
                    className={`relative overflow-hidden rounded-xl cursor-pointer border-2 transition-all ${selectedImage === i ? 'border-amber-500' : 'border-transparent'}`}
                    style={{ paddingBottom: '100%' }}>
                    <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover object-top" />
                  </button>
                ))}
              </div>
            </div>

            {/* Info */}
            <div>
              <p className="text-amber-600 text-sm font-medium mb-2">{product.category}</p>
              <h1 className="text-3xl lg:text-4xl font-bold text-stone-900 mb-4 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>{product.name}</h1>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center gap-0.5">
                  {[1,2,3,4,5].map(s => (
                    <i key={s} className={`text-sm ${s <= Math.floor(product.rating) ? 'ri-star-fill text-amber-400' : 'ri-star-line text-stone-300'}`} />
                  ))}
                </div>
                <span className="text-stone-500 text-sm">{product.rating} ({product.reviewCount} reviews)</span>
              </div>
              <div className="flex items-center gap-3 mb-6">
                <span className="text-4xl font-bold text-stone-900">${product.price}</span>
                {product.originalPrice && <span className="text-xl text-stone-400 line-through">${product.originalPrice}</span>}
              </div>
              <p className="text-stone-600 leading-relaxed mb-8">{product.shortDescription}</p>

              {/* Quantity */}
              <div className="flex items-center gap-4 mb-6">
                <span className="text-sm font-medium text-stone-700">Quantity</span>
                <div className="flex items-center border border-stone-200 rounded-xl overflow-hidden">
                  <button onClick={() => setQuantity(q => Math.max(1, q - 1))}
                    className="w-10 h-10 flex items-center justify-center hover:bg-stone-100 transition-colors cursor-pointer text-stone-600">
                    <i className="ri-subtract-line" />
                  </button>
                  <span className="w-12 text-center font-semibold text-stone-900">{quantity}</span>
                  <button onClick={() => setQuantity(q => q + 1)}
                    className="w-10 h-10 flex items-center justify-center hover:bg-stone-100 transition-colors cursor-pointer text-stone-600">
                    <i className="ri-add-line" />
                  </button>
                </div>
              </div>

              <button onClick={handleAddToCart}
                className={`w-full py-4 rounded-xl font-semibold text-lg transition-all mb-3 whitespace-nowrap ${added ? 'bg-green-500 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white'}`}>
                {added ? '✓ Added to Cart!' : 'Add to Cart'}
              </button>

              <div className="grid grid-cols-3 gap-3 mt-6">
                {[
                  { icon: 'ri-truck-line', text: 'Free Shipping' },
                  { icon: 'ri-shield-check-line', text: 'Secure Payment' },
                  { icon: 'ri-refresh-line', text: '30-Day Returns' },
                ].map(f => (
                  <div key={f.text} className="flex flex-col items-center gap-1.5 p-3 bg-stone-50 rounded-xl text-center">
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className={`${f.icon} text-amber-600 text-lg`} />
                    </div>
                    <span className="text-xs text-stone-600 font-medium">{f.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-stone-200 mb-8">
            <div className="flex gap-8">
              {['description', 'specs', 'reviews'].map(tab => (
                <button key={tab} onClick={() => setActiveTab(tab)}
                  className={`pb-3 text-sm font-medium capitalize transition-colors cursor-pointer whitespace-nowrap border-b-2 -mb-px ${activeTab === tab ? 'border-amber-500 text-amber-600' : 'border-transparent text-stone-500 hover:text-stone-700'}`}>
                  {tab === 'reviews' ? `Reviews (${product.reviewCount})` : tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {activeTab === 'description' && (
            <div className="max-w-3xl">
              <p className="text-stone-600 leading-relaxed text-base">{product.description}</p>
            </div>
          )}

          {activeTab === 'specs' && (
            <div className="max-w-2xl">
              <div className="divide-y divide-stone-100">
                {Object.entries(product.specs).map(([key, val]) => (
                  <div key={key} className="flex py-3 gap-8">
                    <span className="w-40 text-sm font-medium text-stone-500 flex-shrink-0">{key}</span>
                    <span className="text-sm text-stone-900">{val}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div className="max-w-3xl space-y-6">
              <div className="flex items-center gap-6 p-6 bg-stone-50 rounded-2xl mb-8">
                <div className="text-center">
                  <p className="text-5xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>{product.rating}</p>
                  <div className="flex items-center gap-0.5 justify-center mt-1">
                    {[1,2,3,4,5].map(s => <i key={s} className={`text-sm ${s <= Math.floor(product.rating) ? 'ri-star-fill text-amber-400' : 'ri-star-line text-stone-300'}`} />)}
                  </div>
                  <p className="text-stone-400 text-xs mt-1">{product.reviewCount} reviews</p>
                </div>
              </div>
              {product.reviews.map(review => (
                <div key={review.id} className="border-b border-stone-100 pb-6">
                  <div className="flex items-start gap-4">
                    <img src={review.avatar} alt={review.author} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-stone-900 text-sm">{review.author}</span>
                        <span className="text-stone-400 text-xs">{review.date}</span>
                      </div>
                      <div className="flex items-center gap-0.5 mb-2">
                        {[1,2,3,4,5].map(s => <i key={s} className={`text-xs ${s <= review.rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-stone-300'}`} />)}
                      </div>
                      <p className="font-medium text-stone-800 text-sm mb-1">{review.title}</p>
                      <p className="text-stone-600 text-sm leading-relaxed">{review.content}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
